// GR05 조회조건 저장/복원 — 순수 변환 (index.tsx 에서 소비, 테스트 대상)
//
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 경로는 `MenuStorage.menuOnSession.setSearch` → `Utils.qsStringify`(arrayFormat: comma),
// 복원 경로는 `useParsedQuery`(qs.parse comma) 다. 그래서 왕복 형태를 그 두 함수에 맞춘다.
//
// 폼 값을 그대로 저장하지 않는 이유는 날짜다 — `Dayjs` 객체는 쿼리스트링으로 직렬화되지 않는다.
// 요청 DTO(GetAllInboundsReq)와 같은 `yyyyMMdd` 문자열로 눕혔다가 복원 시 다시 `dayjs` 로 세운다.
import dayjs from 'dayjs'
import { ALL_SERVICE_TYPES_VALUE, FIELD_NAMES } from './constants'
import type { AllInboundStatusFormValues } from './filterToRequest'

const DATE_FORMAT = 'YYYYMMDD'

/**
 * 화주유형 '전체' 의 저장 토큰. 화면 값은 빈 문자열(`ALL_SERVICE_TYPES_VALUE`, 산출물 멀티셀렉트 allOption 계약)인데
 * 빈 문자열은 `toList` 가 버려 URL·세션에 남지 않았다 — '전체' 로 조회하고 탭을 다녀오면 필터가 기본값으로 돌아가고
 * 복원 조회도 안 나갔다(PR 리뷰 지적 2026-09-10). main 이 쓰던 'ALL' 을 저장 토큰으로 두고 복원 때 빈 문자열로 되돌린다.
 */
const ALL_SERVICE_TYPES_TOKEN = 'ALL'
const toStoredTypes = (v?: string[]) => v?.map((x) => (x === ALL_SERVICE_TYPES_VALUE ? ALL_SERVICE_TYPES_TOKEN : x))
const fromStoredTypes = (v?: string[]) => v?.map((x) => (x === ALL_SERVICE_TYPES_TOKEN ? ALL_SERVICE_TYPES_VALUE : x))

/** URL·세션에 남는 형태. 값이 있는 키만 싣는다(qsStringify 가 nil 은 걸러 주지만 빈 배열·빈 문자열은 남긴다). */
export interface AllInboundSearchQuery {
  logisticsServiceTypes?: string[]
  shipperNames?: string[]
  ordOptionNumbers?: string[]
  plnDateFrom?: string
  plnDateTo?: string
  actDate?: string
  orderType?: string
  orderStatus?: string
}

/** 폼 값이 배열/단건 문자열 어느 쪽으로 와도 문자열 목록으로 맞춘다(mfs.FormTextArea 는 양쪽 다 준다). */
const toList = (v?: string[] | string): string[] | undefined => {
  const list = Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v.trim()] : []
  const cleaned = list.map((x) => String(x).trim()).filter(Boolean)
  return cleaned.length ? cleaned : undefined
}

/** 날짜 필드에서 [시작, 끝] 문자열을 꺼낸다. 단일 값이면 시작만 채운다. */
const toDateString = (v: unknown): string | undefined => {
  if (!v) return undefined
  // `as never` 로 우회하지 않는다 — 시그니처가 바뀌어도 컴파일러가 못 잡는다.
  // 이 함수가 실제로 받는 값(폼의 Dayjs, 복원 경로의 문자열)만 열어 둔다.
  const d = dayjs(v as dayjs.ConfigType)
  return d.isValid() ? d.format(DATE_FORMAT) : undefined
}

/**
 * 폼 값 → 저장 형태. `undefined` 키는 qsStringify 가 걸러 낸다.
 * 페이지는 싣지 않는다 — 전량을 받아 화면이 나누므로 페이지 이동이 조회 조건이 아니다(LOGISPRD-15268).
 */
export const toSearchQuery = (values: AllInboundStatusFormValues): AllInboundSearchQuery => {
  const pln = values[FIELD_NAMES.plnDateRange]
  const [plnFrom, plnTo] = Array.isArray(pln) ? [pln[0], pln[1]] : [pln, pln]
  const act = values[FIELD_NAMES.actDate]

  return {
    logisticsServiceTypes: toList(toStoredTypes(values[FIELD_NAMES.logisticsServiceTypes])),
    // 화주명의 '전체'(빈 문자열)는 저장하지 않는다 — 키가 없으면 복원 때 필터가 '전체' 로 그린다(AllInboundFilter 의 빈 목록 = '전체').
    shipperNames: toList(values[FIELD_NAMES.shipperNames]),
    ordOptionNumbers: toList(values[FIELD_NAMES.ordOptionNumbers]),
    plnDateFrom: toDateString(plnFrom),
    plnDateTo: toDateString(plnTo),
    actDate: toDateString(Array.isArray(act) ? act[0] : act),
    orderType: values[FIELD_NAMES.orderType] || undefined,
    orderStatus: values[FIELD_NAMES.orderStatus] || undefined,
  }
}

/**
 * 문자열 목록만 남긴다 — comma 파서는 값이 하나면 배열이 아니라 문자열을 준다(`a,b` → `['a','b']`, `a` → `'a'`).
 * 조작된 URL(객체·중첩 배열)은 버린다.
 */
export const asRestoredList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v)) {
    const cleaned = v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String)
    return cleaned.length ? cleaned : undefined
  }
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

/**
 * `yyyyMMdd` 문자열 → Dayjs. 형식이 어긋나면 버린다(조작된 URL 이 날짜 칸을 Invalid Date 로 만들지 않게).
 * 레포에 dayjs customParseFormat 플러그인이 없어 ISO 형태로 바꿔 넘긴다(ST11 재고이동내역 준용).
 */
const asRestoredDate = (v: unknown) => {
  if (typeof v !== 'string' || !/^\d{8}$/.test(v)) return null
  const parsed = dayjs(`${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6)}`)
  return parsed.isValid() ? parsed : null
}

export interface RestoredSearch {
  values: AllInboundStatusFormValues
}

/**
 * URL 쿼리(파싱 결과) → 복원 조회조건.
 *
 * 화주 필터(화주유형 또는 화주명)가 없으면 복원 대상이 아니다(undefined) — 그 상태로 조회하면
 * 어차피 BE 필수 필터에 걸려 400 이라, 기본값으로 여는 편이 맞다(MA02 준용).
 */
export const fromSearchQuery = (query: Record<string, unknown>): RestoredSearch | undefined => {
  const logisticsServiceTypes = fromStoredTypes(asRestoredList(query.logisticsServiceTypes))
  const shipperNames = asRestoredList(query.shipperNames)
  if (!logisticsServiceTypes && !shipperNames) return undefined

  const plnFrom = asRestoredDate(query.plnDateFrom)
  const plnTo = asRestoredDate(query.plnDateTo)

  return {
    values: {
      // 초기화가 setFieldsValue 로 덮어쓰므로 비는 항목도 키를 남긴다(buildDefaultValues 와 같은 이유).
      [FIELD_NAMES.logisticsServiceTypes]: logisticsServiceTypes ?? [],
      [FIELD_NAMES.shipperNames]: shipperNames ?? [],
      [FIELD_NAMES.ordOptionNumbers]: asRestoredList(query.ordOptionNumbers) ?? [],
      // 한쪽만 남아 있어도 그 날짜만 세운다 — 요청 변환(toRange)이 null 을 걸러 준다.
      [FIELD_NAMES.plnDateRange]: plnFrom || plnTo ? [plnFrom, plnTo] : null,
      [FIELD_NAMES.actDate]: asRestoredDate(query.actDate),
      [FIELD_NAMES.orderType]: typeof query.orderType === 'string' ? query.orderType : '',
      [FIELD_NAMES.orderStatus]: typeof query.orderStatus === 'string' ? query.orderStatus : '',
    },
  }
}
